import { Controller } from '@hotwired/stimulus'

// Multi-select autocomplete controller for related terms/articles
// Usage: data-controller="admin--glossary--multi-select"
//        data-admin--glossary--multi-select-url-value="/admin/glossary/terms/search_terms"
//        data-admin--glossary--multi-select-name-value="related_term_ids[]"
export default class extends Controller {
  static targets = ['input', 'results', 'selected', 'hiddenInputs']

  static values = {
    url: String,
    name: String,
    labelKey: { type: String, default: 'term' },
    subtextKey: { type: String, default: 'short_definition' }
  }

  connect() {
    this.debounceTimer = null
    this.closeOnClickOutside = this.closeOnClickOutside.bind(this)
  }

  disconnect() {
    this.clearDebounce()
    document.removeEventListener('click', this.closeOnClickOutside)
  }

  search() {
    this.clearDebounce()
    const query = this.inputTarget.value.trim()

    if (query.length < 2) {
      this.hideResults()
      return
    }

    this.debounceTimer = setTimeout(() => {
      this.fetchResults(query)
    }, 300)
  }

  async fetchResults(query) {
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('q', query)

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`)
      }

      const data = await response.json()
      this.renderResults(data)
    } catch (error) {
      console.error('Search failed:', error)
      this.hideResults()
    }
  }

  renderResults(items) {
    // Filter out already selected items
    const selectedIds = this.getSelectedIds()
    const available = items.filter((item) => !selectedIds.includes(String(item.id)))

    if (available.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="px-3 py-2 text-sm text-slate-500">
          No results found
        </div>
      `
      this.showResults()
      return
    }

    const html = available.map((item) => {
      const label = item[this.labelKeyValue] || item.title || item.name
      const subtext = item[this.subtextKeyValue] || item.excerpt || ''
      return `
        <button type="button"
                class="w-full text-left px-3 py-2 hover:bg-slate-100 border-b border-slate-200 last:border-b-0"
                data-action="click->admin--glossary--multi-select#selectItem"
                data-id="${item.id}"
                data-label="${this.escapeAttr(label)}">
          <div class="font-medium text-sm text-slate-900">${this.escapeHtml(label)}</div>
          ${subtext ? `<div class="text-xs text-slate-500 truncate">${this.escapeHtml(subtext)}</div>` : ''}
        </button>
      `
    }).join('')

    this.resultsTarget.innerHTML = html
    this.showResults()
  }

  selectItem(event) {
    event.preventDefault()
    const button = event.currentTarget
    const id = button.dataset.id
    const label = button.dataset.label

    // Add to selected list
    this.addSelected(id, label)

    // Clear input and hide results
    this.inputTarget.value = ''
    this.hideResults()
  }

  addSelected(id, label) {
    // Create tag element
    const tag = document.createElement('div')
    tag.className = 'inline-flex items-center gap-1 px-2 py-1 bg-slate-100 border border-slate-300 text-sm'
    tag.dataset.id = id
    tag.innerHTML = `
      <span>${this.escapeHtml(label)}</span>
      <button type="button"
              class="text-slate-500 hover:text-red-600"
              data-action="click->admin--glossary--multi-select#removeItem">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    `
    this.selectedTarget.appendChild(tag)

    // Add hidden input
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = this.nameValue
    input.value = id
    input.dataset.forId = id
    this.hiddenInputsTarget.appendChild(input)
  }

  removeItem(event) {
    event.preventDefault()
    const tag = event.currentTarget.closest('[data-id]')
    const id = tag.dataset.id

    // Remove hidden input
    const input = this.hiddenInputsTarget.querySelector(`input[data-for-id="${id}"]`)
    if (input) {
      input.remove()
    }

    // Remove tag
    tag.remove()
  }

  getSelectedIds() {
    return Array.from(this.hiddenInputsTarget.querySelectorAll('input'))
      .map((input) => input.value)
  }

  showResults() {
    this.resultsTarget.classList.remove('hidden')
    document.addEventListener('click', this.closeOnClickOutside)
  }

  hideResults() {
    this.resultsTarget.classList.add('hidden')
    document.removeEventListener('click', this.closeOnClickOutside)
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }

  clearDebounce() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  escapeAttr(text) {
    return text.replace(/"/g, '&quot;').replace(/'/g, '&#39;')
  }
}
